import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import AppMenu from '../components/AppMenu';
import api from '../services/api';

interface PaymentStatusResponse {
  message: string;
  sessionId: string;
}

const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get<PaymentStatusResponse>(`/payments/success?session_id=${sessionId}`);
        setPaymentStatus(response.data);
      } catch (err) {
        console.error('Error fetching payment status:', err);
        setError('Failed to verify payment status');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
  }, [sessionId]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewBookings = () => {
    navigate('/my-bookings');
  };

  if (loading) {
    return (
      <Box>
        <AppMenu />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} thickness={4} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !paymentStatus) {
    return (
      <Box>
        <AppMenu />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: 'background.paper',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Typography variant="h6" color="error">
                {error || 'Payment verification failed'}
              </Typography>
              <Button
                variant="contained"
                onClick={handleViewBookings}
                sx={{
                  bgcolor: '#EC4899',
                  '&:hover': {
                    bgcolor: '#DB2777',
                  },
                }}
              >
                View My Bookings
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <AppMenu />
      
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={4} alignItems="center">
            {/* Success Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 50,
                  color: 'white',
                }}
              />
            </Box>

            {/* Success Message */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                {paymentStatus.message}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                Your booking has been confirmed and you should receive a confirmation email shortly.
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleViewBookings}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: '#EC4899',
                  '&:hover': {
                    bgcolor: '#DB2777',
                  },
                }}
              >
                View My Bookings
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleGoHome}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderColor: '#EC4899',
                  color: '#EC4899',
                  '&:hover': {
                    borderColor: '#DB2777',
                    color: '#DB2777',
                    bgcolor: 'transparent',
                  },
                }}
              >
                Go to Home
              </Button>
            </Stack>

            {/* Session ID (for debugging) */}
            {paymentStatus.sessionId && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  mt: 2,
                }}
              >
                Session ID: {paymentStatus.sessionId}
              </Typography>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default CheckoutSuccess;

